import React, { type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import { Flex, FormElement, InputText, Select } from 'mailup-web-components'

import type { NewRecipient } from 'Pages/RecipientsList/model'
import { panelAddRecipientChangeFieldCallback } from 'Pages/RecipientsList/recoil/callbacks/addRecipient'
import {
  panelAddRecipientIsEmailValidState,
  panelAddRecipientIsMobileValidState,
  panelAddRecipientIsRequiredFeedbackState,
  panelAddRecipientState,
} from 'Pages/RecipientsList/recoil/state/addRecipient'
import { countryCodesState } from 'Pages/RecipientsList/recoil/state/common'

const BoxAddFixedFields: FC = () => {
  const intl = useIntl()

  const {
    isDirty,
    recipient: { Email, MobilePrefix, MobileNumber },
  } = useRecoilValue(panelAddRecipientState)

  const { items: countryCodes } = useRecoilValue(countryCodesState)

  const isValidEmail = useRecoilValue(panelAddRecipientIsEmailValidState)
  const isValidMobile = useRecoilValue(panelAddRecipientIsMobileValidState)
  const requiredFeedback = useRecoilValue(
    panelAddRecipientIsRequiredFeedbackState,
  )

  const changeField = useRecoilCallback(
    panelAddRecipientChangeFieldCallback,
    [],
  )

  const handleChangeField =
    (f: keyof NewRecipient) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      void changeField({
        [f]: e.target.value,
      })
    }

  const handleChangePrefix = (v: string | undefined): void => {
    void changeField({
      MobilePrefix: v !== undefined ? v : '',
    })
  }

  const emailFormatFeedback = isDirty && !isValidEmail
  const mobileFormatFeedback = isDirty && !isValidMobile

  const emailFeedback = emailFormatFeedback
    ? intl.formatMessage({ id: 'Load.Common.WrongFormatWarning' })
    : requiredFeedback
      ? intl.formatMessage({
          id: 'Load.RecipientsList.AddRecipientRequiredFieldMessage',
        })
      : undefined

  const mobileFeedback = mobileFormatFeedback
    ? intl.formatMessage({ id: 'Load.Common.ValidationWrongFormat' })
    : requiredFeedback
      ? intl.formatMessage({
          id: 'Load.RecipientsList.AddRecipientRequiredFieldMessage',
        })
      : undefined

  return (
    <Flex flexDirection="column" gap="16px">
      <FormElement
        label={intl.formatMessage({ id: 'Load.RecipientsList.ColumnEmail' })}
        feedback={emailFeedback}
        feedbackVariant={emailFeedback !== undefined ? 'error' : undefined}
      >
        <InputText
          id="Email"
          name="Email"
          value={Email}
          onChange={handleChangeField('Email')}
          data-mu-test="recipient-add-email"
          variant={emailFeedback !== undefined ? 'error' : undefined}
          maxLength={100}
        />
      </FormElement>

      <FormElement
        label={intl.formatMessage({
          id: 'Load.RecipientsList.ColumnMobilePrefix',
        })}
      >
        <Select
          value={MobilePrefix}
          options={countryCodes}
          onChange={handleChangePrefix}
          data-mu-test="recipient-add-mobile-prefix"
          error={mobileFeedback !== undefined}
          placeholder={intl.formatMessage({
            id: 'Load.MailupCommon.Select',
          })}
        />
      </FormElement>

      <FormElement
        label={intl.formatMessage({
          id: 'Load.RecipientsList.ColumnMobileNumber',
        })}
        feedback={mobileFeedback}
        feedbackVariant={mobileFeedback !== undefined ? 'error' : undefined}
      >
        <InputText
          id="MobileNumber"
          name="MobileNumber"
          value={MobileNumber}
          onChange={handleChangeField('MobileNumber')}
          data-mu-test="recipient-add-mobile-number"
          variant={mobileFeedback !== undefined ? 'error' : undefined}
        />
      </FormElement>
    </Flex>
  )
}

export default BoxAddFixedFields
